import "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { SubscriptionStatus } from "@/components/subscription-status";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { ArrowLeftIcon, CreditCardIcon, SettingsIcon } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";

export const Route = createFileRoute("/_app/subscription")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();

  const { mutateAsync, isPending } = useMutation(
    trpc.stripe.createBillingPortalSession.mutationOptions({
      onError: (error) => {
        toast.error("Failed to create billing portal session", {
          description: error.message,
        });
      },
    }),
  );

  const redirectToBilling = async () => {
    const { url } = await mutateAsync();
    if (url) {
      window.location.href = url;
    }
  };

  return (
    <main>
      <div className="min-h-screen px-4 py-8">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-8 flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: "/" })}
              className="flex items-center gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>

          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold text-white">Subscription & Billing</h1>
            <p className="text-gray-200">Manage your subscription plan and billing information</p>
          </div>

          <div className="grid gap-6">
            <SubscriptionStatus onUpgrade={() => {}} />

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <SettingsIcon className="h-5 w-5" />
                  Billing Management
                </CardTitle>
                <CardDescription>
                  Update payment methods, download invoices, and view billing history
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Button
                    variant="outline"
                    isLoading={isPending}
                    onClick={redirectToBilling}
                    className="flex items-center gap-2"
                  >
                    <CreditCardIcon className="h-4 w-4" />
                    Manage Billing
                  </Button>
                  <p className="text-sm text-gray-600">
                    This will redirect you to a secure billing portal where you can update your
                    payment method, download invoices, and manage your subscription.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Plan Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Available Plans</CardTitle>
                <CardDescription>
                  Compare features across different subscription tiers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border p-4">
                    <h3 className="mb-2 font-semibold">Free Plan</h3>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Up to 3 projects</li>
                      <li>• Basic templates</li>
                      <li>• Email support</li>
                      <li>• Community access</li>
                    </ul>
                  </div>

                  <div className="rounded-lg border bg-blue-50 p-4">
                    <h3 className="mb-2 font-semibold text-blue-800">Pro Plan</h3>
                    <ul className="space-y-1 text-sm text-blue-700">
                      <li>• Unlimited projects</li>
                      <li>• Premium templates</li>
                      <li>• Priority support</li>
                      <li>• Advanced analytics</li>
                      <li>• Custom branding</li>
                      <li>• API access</li>
                      <li>• Team collaboration</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6">
                  <Button asChild>
                    <Link to="/pricing">View All Plans</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Usage Information */}
            <Card>
              <CardHeader>
                <CardTitle>Usage Statistics</CardTitle>
                <CardDescription>
                  Monitor your current usage across different features
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Projects Created</span>
                    <span className="text-sm font-medium">2 / 3 (Free Plan)</span>
                  </div>

                  <div className="h-2 w-full rounded-full bg-gray-200">
                    <div className="h-2 rounded-full bg-blue-500" style={{ width: "66%" }}></div>
                  </div>

                  <p className="text-xs text-gray-600">
                    You're using 2 out of 3 available project slots. Upgrade to Pro for unlimited
                    projects.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
}
